import { apiClient } from '@/services/api';
import { API_ERROR_CODE, ApiError } from '@/services/errors';

export interface PublicSignupRequest {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  school?: string;
  /** Tracked-link token (`?r=<token>`). */
  r?: string;
  /** Legacy referral path (`?ref=<ref_id>`). */
  ref_id?: string;
  /** Back-compat with `app.marketpryce.com/<deal_slug>?r=<token>` URLs. */
  deal_slug?: string;
  /**
   * Season of Gifting signup variant. When `campaign` matches the server's
   * current campaign AND the campaign is configured server-side, the server
   * requires school+sport+instagram, sets athlete/signup_campaign, and
   * forces the new_generic (PIN) outcome. Otherwise these are ignored and
   * the request behaves as a regular signup.
   */
  campaign?: string;
  sport?: string;
  instagram_handle?: string;
  tiktok_handle?: string;
}

/**
 * Marketing copy bundle stored as jsonb on the Deal record. All fields
 * optional — components render fallbacks when their field is missing.
 * Schema is validated on the server by Deals::MarketingCopySchema.
 */
export interface MarketingCopy {
  eyebrow?: string;
  hero_subhead?: string;
  hero_body_lines?: string[];
  how_it_works?: { title: string; body: string }[];
  faq?: { q: string; a: string }[];
  compliance_disclosure?: string;
  /**
   * Public signup landing copy (`/[slug]` + in-app signup card). Each
   * field is optional; SignupCard falls back to its generic template
   * per field. Strings may wrap phrases in `**x**` — rendered in accent
   * green by <HighlightedText>.
   */
  landing?: {
    headline?: string;
    subhead?: string;
    callout?: { title: string; body: string };
  };
}

/**
 * Public deal payload. Single source of truth for the `deal:` shape
 * returned by both `referral_invites#show` and `deals#show`. Backed by
 * `Api::V1::Public::DealPayload.serialize` in the Rails app — keep
 * these in sync.
 */
export interface PublicSignupDeal {
  slug: string;
  brand_name: string;
  /** Offer name that tells sibling deals apart ("SoFi Invest"); brand_name is the company ("SoFi"). */
  display_name: string;
  brand_category: string | null;
  title: string;
  description: string | null;
  terms: string | null;
  user_cut_cents: number;
  referrer_cut_cents: number;
  cover_image_url: string | null;
  brand_logo_url: string | null;
  marketing_copy: MarketingCopy;
  // Monthly referral cap (see backend DealPayload). Optional so older fixtures
  // and any pre-cap server stay valid; a real response always includes them.
  // When `full`, the deal is out of monthly spots: the signup still creates the
  // account, but there's no redirect — the landing shows a "reopens on the 1st"
  // state instead. null cap = uncapped.
  monthly_referral_cap?: number | null;
  spots_used?: number | null;
  spots_left?: number | null;
  full?: boolean;
  cap_resets_at?: string | null;
}

/**
 * Stable response shape from `POST /api/v1/public/signup`. Three
 * outcomes — `new_deal_anchored`, `new_generic`, `existing` —
 * discriminated by the `outcome` field. The other fields are non-null
 * only when they apply to that outcome.
 *
 * - new_deal_anchored: net-new user, deal resolved. `redirect_url` is
 *   the affiliate URL the success screen opens immediately. No PIN was
 *   sent (user is leaving for the brand).
 * - new_generic: net-new user, no deal resolved. Server triggered
 *   PinService.request_pin internally — client routes to PIN entry.
 * - existing: phone or email matched an existing User. Client
 *   redirects to log-in pre-filled with `contact_masked`.
 *
 * No session token is ever returned; the public-signup endpoint never
 * authenticates the caller.
 */
export type PublicSignupOutcome = 'new_deal_anchored' | 'new_generic' | 'existing';

export interface PublicSignupResponse {
  ok: true;
  outcome: PublicSignupOutcome;
  deal: PublicSignupDeal | null;
  /** Phone or email mask the client can show in confirmation copy / pre-fill banners. */
  contact_masked: string | null;
  /** Affiliate URL on `new_deal_anchored`; null for the other outcomes. */
  redirect_url: string | null;
}

export interface ReferralInviteResponse {
  inviter: {
    first_name: string | null;
    public_name: string;
  };
  /**
   * The link's anchored deal — same shape as `deals#show`. Null when
   * the link's deal is no longer currently_visible (archived between
   * page render and form submit). The link is the user's intent of
   * record; we don't silently swap to a different deal.
   */
  deal: PublicSignupDeal | null;
}

/**
 * Public, unauthenticated client for the auth-flow-redesign signup
 * surface (web `/<slug>?r=<token>` + mobile `/(auth)/signup`). All
 * endpoints sit under `/api/v1/public/*` and don't require a session.
 */
export const publicSignupService = {
  async signup(payload: PublicSignupRequest): Promise<PublicSignupResponse> {
    return apiClient.post<PublicSignupResponse>('/api/v1/public/signup', payload);
  },

  /**
   * Fetch inviter (and optionally the link's anchored deal) for the
   * public deal-landing page. `identifier` may be a ReferralLink token
   * (`?r=<token>` URLs — returns inviter + deal) OR a legacy `ref_id /
   * handle / email` value (`?ref=<value>` URLs from the Webflow 301 +
   * old email blasts — returns inviter only; deal context comes from
   * the slug). Backend `Public::ReferralInvitesController#show` falls
   * back from token lookup to `Users::Creator.lookup_user_by_ref` so
   * one endpoint serves both shapes.
   */
  async invite(identifier: string, dealSlug?: string): Promise<ReferralInviteResponse> {
    const query = dealSlug ? `?deal_slug=${encodeURIComponent(dealSlug)}` : '';
    return apiClient.get<ReferralInviteResponse>(
      `/api/v1/public/referral_invites/${encodeURIComponent(identifier)}${query}`,
    );
  },

  /**
   * Fetch a deal by slug for the public landing page when there's no
   * referral token. Returns null on 404 (slug doesn't exist or deal
   * isn't currently visible). Other errors throw — callers decide
   * whether to retry or show an error state.
   */
  async fetchDealBySlug(slug: string): Promise<PublicSignupDeal | null> {
    try {
      return await apiClient.get<PublicSignupDeal>(
        `/api/v1/public/deals/${encodeURIComponent(slug)}`,
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === API_ERROR_CODE.NotFound) return null;
      throw error;
    }
  },
};
